import { memo, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { Encounter, Resource } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Accordion, Button, Flex, Group, Menu, Paper, Stack } from "@mantine/core";

import { IconNotes, IconPlus } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";
import { EncounterContext } from "@/contexts/encounter";
import { usePractitionerPermissions } from "@/permission/hooks/usePractitionerPermissions";
import { useAppDispatch } from "@/store";
import { stopResourcePolling } from "@/store/slices/resource-polling";
import { PoolingKeys } from "@/types/pooling";
import useResourcePolling from "@/utils/hooks/use-resource-polling";
import { getEncounterType } from "@/utils/str";

import {
  PHYSICAL_EXAMS_BY_URL,
  PhysicalExamQuestionnaireURL,
} from "./constants/physical-exam-registrations";
import classes from "./PhysicalExams.module.css";
import { addPhysicalExamToEncounter, fetchPhysicalExamsOptions } from "./services/physical-exams";
import { PhysicalExamOption, PhysicalExamProps } from "./types/physical-exams";
import { getActivePhysicalExamsURLs } from "./utils/physical-exams";

type PhysicalExamComponent = [string, React.ComponentType<PhysicalExamProps>];

interface PhysicalExamsProps {
  submitTrigger: number;
  isEncounterActive: boolean;
  isAmendment?: boolean;
}

function PhysicalExams({ submitTrigger, isEncounterActive, isAmendment }: PhysicalExamsProps) {
  const medplum = useMedplum();
  const { hasProviderOrScribeRole } = usePractitionerPermissions();
  const { encounter, setEncounter } = useContext(EncounterContext);
  const [physicalExamsOptions, setPhysicalExamsOptions] = useState<PhysicalExamOption[]>([]);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!encounter) return;

    const encounterType = getEncounterType(encounter);

    (async () => {
      if (encounterType) {
        const physicalExams = await fetchPhysicalExamsOptions(medplum, encounterType);
        setPhysicalExamsOptions(physicalExams);
      }
    })();
  }, [encounter, medplum]);

  useResourcePolling(
    PoolingKeys.EncounterPhysicalExams,
    "Encounter",
    useMemo(() => ({ _id: encounter?.id }), [encounter]),
    useCallback(
      async (resources: Resource[]) => {
        const encounter = resources[0] as Encounter;
        setEncounter(encounter);
      },
      [setEncounter],
    ),
    useCallback(
      (error: Error) => {
        console.error(error);
        dispatch(stopResourcePolling(PoolingKeys.EncounterPhysicalExams));
      },
      [dispatch],
    ),
    { maxExecutions: 3 },
  );

  const [activePhysicalExams, activePhysicalExamURLs, hasActivePhysicalExams] = useMemo(() => {
    if (!encounter) return [[], [], false];

    const physicalExamURLs = getActivePhysicalExamsURLs(encounter);

    // Map URLs to components - the search filtering ensures we only get appropriate exams
    const physicalExams = (
      physicalExamURLs?.map((url) => {
        const registration = PHYSICAL_EXAMS_BY_URL[url as PhysicalExamQuestionnaireURL];
        return registration ? [url, registration.Component] : null;
      }) ?? []
    ).filter((item) => item !== null) as PhysicalExamComponent[];

    return [physicalExams, physicalExamURLs, physicalExams.length > 0];
  }, [encounter]);

  // Physical exam options are now filtered by encounter service type at the service level
  const filteredPhysicalExamsOptions = physicalExamsOptions;

  const handleAddPhysicalExam = async (physicalExamUrl: string) => {
    if (!encounter) return;

    const updatedEncounter = await addPhysicalExamToEncounter(medplum, encounter, physicalExamUrl);
    setEncounter(updatedEncounter);
  };

  const editable = (isEncounterActive || isAmendment) && hasProviderOrScribeRole;

  // In the read-only (locked) view, every added section starts expanded so the note can be read
  // without clicking each panel. In editable mode, and while the encounter is still active
  // (in progress, not yet locked), sections start collapsed.
  const defaultOpenAccordionValues = useMemo(() => {
    if (editable || isEncounterActive) return undefined;

    return activePhysicalExamURLs.reduce<string[]>((values, url) => {
      const registration = PHYSICAL_EXAMS_BY_URL[url as PhysicalExamQuestionnaireURL];
      if (registration) {
        values.push(registration.accordionValue);
      }
      return values;
    }, []);
  }, [activePhysicalExamURLs, editable, isEncounterActive]);

  return (
    <Stack
      gap="xs"
      p={isAmendment ? "lg" : ""}
      bg={isAmendment ? "white" : ""}
      style={{ borderRadius: isAmendment ? 8 : 0 }}
    >
      <Group justify="space-between">
        <TextWrapper c="dark.9" variant="bold">
          Physical Exams
        </TextWrapper>
        {editable && (
          <Menu width={200} shadow="sm" position="bottom-end">
            <Menu.Target>
              <Button variant="outline" color="burgundy.9" leftSection={<IconPlus size={16} />}>
                Add More
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {filteredPhysicalExamsOptions.map((physicalExamsOption) => (
                <Menu.Item
                  py="xs"
                  key={physicalExamsOption.id}
                  onClick={() => handleAddPhysicalExam(physicalExamsOption.url)}
                  data-testid={`physical-exam-option-${physicalExamsOption.title}`}
                  disabled={activePhysicalExamURLs?.includes(physicalExamsOption.url)}
                >
                  <TextWrapper variant="bold">{physicalExamsOption.title}</TextWrapper>
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>
      <Paper withBorder radius={"md"} style={{ background: "white", padding: 16 }}>
        {hasActivePhysicalExams ? (
          <Accordion
            multiple
            data-testid="physical-exams-accordion"
            defaultValue={defaultOpenAccordionValues}
            classNames={{ control: classes.control }}
            styles={{
              label: { padding: 0 },
              content: { padding: 0 },
            }}
          >
            <Stack gap="xs">
              {activePhysicalExams.map(([url, Component]) => (
                <Component
                  key={url}
                  url={url}
                  editable={editable}
                  submitTrigger={submitTrigger}
                  isAmendment={!isEncounterActive}
                />
              ))}
            </Stack>
          </Accordion>
        ) : (
          <EmptyPhysicalExamsList />
        )}
      </Paper>
    </Stack>
  );
}

export function EmptyPhysicalExamsList() {
  return (
    <Flex
      h={40}
      gap="sm"
      w="100%"
      bg="gray.0"
      align="center"
      justify="center"
      style={{ borderRadius: "var(--mantine-radius-xs)" }}
    >
      <IconNotes size={18} color="var(--mantine-color-gray-6)" />
      <TextWrapper fz="md" c="gray.7" variant="default">
        No physical exam has been added.
      </TextWrapper>
    </Flex>
  );
}

export default memo(PhysicalExams, (prevProps, nextProps) => {
  return (
    prevProps.isAmendment === nextProps.isAmendment &&
    prevProps.isEncounterActive === nextProps.isEncounterActive &&
    prevProps.submitTrigger === nextProps.submitTrigger
  );
});
